import { localizedStrings } from '@core/strings'
import React from 'react'
import { Flex } from '@fundamentals'
import { SignatureEdit } from 'webapp/components/common/Signature'
import {
  DialogActions,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import moment from 'moment'
import { useModal } from '@common/GlobalModal'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { Permit } from '@core/entities/permit/Permit'
import {
  useApprovePermit,
  useNominateApprovers,
} from '@core/react-query/features/permits/permit'
import { ITemplateFormAnswers } from '@core/entities/template/TemplateFormAnswers'
import { ModalDialogContainer } from '@common/modal'
import NominateApproverModal from '@common/NominateApproverModal'
import { DatePicker } from '@common/material/DatePicker'
import { useForm, Controller } from 'react-hook-form'

interface ComponentType {
  permit: Permit
  approvalAnswers?: ITemplateFormAnswers
  onApprove?: () => void
}

type FormData = {
  startDate: string
  endDate: string
}

export const ApprovePermitModal: React.FC<ComponentType> = ({
  permit,
  approvalAnswers,
  onApprove,
}) => {
  const { hideModal, showModal } = useModal()
  const { mutate, isLoading, error, reset } = useApprovePermit()
  const nominateApprovers = useNominateApprovers()

  const { data: profile } = useGetProfile()

  const { control, handleSubmit } = useForm<FormData>({
    defaultValues: {
      startDate: permit.startsOn,
      endDate: permit.endsOn,
    },
  })

  const approvePermit = (data: FormData) => {
    const answers = approvalAnswers?.answers?.map((questionGroup) => {
      const questionGroupData = questionGroup?.data?.map((question) => {
        const submissionProofs = [
          ...(question?.submissionProofs || []),
          // @ts-ignore
          ...(question?.submissionProofFiles || []),
        ]

        const submissionDocuments = [
          ...(question?.submissionDocuments || []),
          // @ts-ignore
          ...(question?.submissionProofDocuments || []),
        ]

        return {
          ...question,
          submissionProofs,
          submissionDocuments,
        }
      })
      return { ...questionGroup, data: questionGroupData }
    })
    mutate(
      {
        permitId: permit.id,
        approvalId: permit.approvalReviews?.nextApprovalId,
        startsOn: data.startDate || permit.startsOn,
        endsOn: data.endDate || permit.endsOn,
        templateId: permit.template.id,
        approvalAnswers: { answers },
      },
      {
        onSuccess: (res) => {
          hideModal()

          const permit = Permit.new(res)

          if (permit.shouldShowNominateApprovers()) {
            showModal(
              <NominateApproverModal
                approvalLabel={permit.getNextApproval()?.approval?.label}
                suggestedApprovers={
                  permit.approvalReviews?.nominatedApprovers
                    ?.suggestedApprovers ?? []
                }
                targetApprovers={
                  permit.approvalReviews?.nominatedApprovers?.approvers?.[0]
                    ?.targetApprovers ?? []
                }
                onDismiss={() => {
                  onApprove && onApprove()
                  hideModal()
                }}
                onNominate={async (approvers) => {
                  await nominateApprovers.mutateAsync({
                    permitId: permit.id,
                    approvers,
                  })
                  onApprove && onApprove()
                  hideModal()
                }}
              />,
            )
          } else {
            onApprove && onApprove()
          }
        },
      },
    )
  }

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.issuePermit}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {!permit.template?.currentVersion?.calculateDurationBasedOnDays && (
          <Flex gap={2}>
            <Controller
              name='startDate'
              control={control}
              render={({ field }) => (
                <DatePicker
                  name='startDate'
                  withTime
                  disablePast
                  sx={{ width: '100%', mt: 1, mb: 3 }}
                  label={localizedStrings.startDateAndTime}
                  value={moment(field.value)}
                  onChange={(v) => {
                    field.onChange(v)
                    if (error) {
                      reset()
                    }
                  }}
                  slotProps={{
                    textField: {
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
            <Controller
              name='endDate'
              control={control}
              render={({ field }) => (
                <DatePicker
                  name='endDate'
                  withTime
                  disablePast
                  sx={{ width: '100%', mt: 1, mb: 3 }}
                  label={localizedStrings.endDateAndTime}
                  value={moment(field.value)}
                  onChange={(v) => {
                    field.onChange(v)
                    if (error) {
                      reset()
                    }
                  }}
                />
              )}
            />
          </Flex>
        )}

        <SignatureEdit readonly value={profile?.signatureUrl} />
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button variant='outlined' onClick={hideModal}>
          {localizedStrings.cancel}
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit(approvePermit)}
          disabled={isLoading}
          data-test='jsConfirmIssue'
        >
          {localizedStrings.approve}
        </Button>
      </DialogActions>
    </ModalDialogContainer>
  )
}
